import { inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import type { Observable } from 'rxjs';
import { BehaviorSubject, defer, merge, of } from 'rxjs';
import { debounceTime, filter, map, take, tap } from 'rxjs/operators';

import { AbilitiesSelectors } from '@core/abilities-data';
import { Ability } from '@core/abilities-types';
import { Analytics } from '@core/analytics-types';
import { AsyncJobActions } from '@core/async-job-data';
import { AsyncJobType } from '@core/async-job-types';
import { buildUrlActionTrigger } from '@core/common-legacy';
import { I18nService } from '@core/i18n-legacy';
import { JobsActions, JobsFullActions, OfferScope } from '@core/offers-data';
import type { OfferItem } from '@core/offers-types';
import { OfferStatus } from '@core/offers-types';
import { NotificationsController, WindowRef } from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import type { DataSourceService, ViewEmptyState, ViewFormPublicService } from '@core/views-types';
import { OfferSlotsCounterComponent } from '@recruitee/billing-smart-ui';
import { SampleDataRemoveContext, SubscriptionService } from '@recruitee/company-logic';
import { JobsAnalytics } from '@recruitee/offers-legacy';
import { JobActionsFacade } from '@recruitee/offers-logic';
import { getJobRouterLink } from '@recruitee/offers-util';
import { SampleDataService } from '@recruitee/sample-data-logic';

import { ActionNeededInfoboxComponent } from '../../offer-page/containers/action-needed-infobox/action-needed-infobox.component';
import { SwitchEditorsInfoboxComponent } from '../../offer-page/containers/switch-editors-infobox/switch-editors-infobox.component';

@Injectable()
export class JobsDataSourceService implements DataSourceService<OfferItem> {
  private readonly i18n = inject(I18nService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly subscription = inject(SubscriptionService);
  public ongoingFollow = new BehaviorSubject<Record<number, boolean>>({});
  public canManageJobs$ = this.store.select(AbilitiesSelectors.hasOne(Ability.ManageJobs));
  public canViewAcquisition$ = this.store.select(
    AbilitiesSelectors.hasOne(Ability.ViewAcquisition),
  );
  public hasMoreSettings$ = this.canManageJobs$;
  public hasSampleData$ = inject(SampleDataService).hasSampleData$;
  public isSampleDataInProgress$ = inject(SampleDataService).sampleDataInProgress$;
  public viewPageHeaderComponent$ = defer(() =>
    of(this.subscription.hasSlotsLimit() ? OfferSlotsCounterComponent : null),
  );
  public infoboxComponent$ = defer(() =>
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      map(event =>
        event.url.includes('jobs:prosemirror')
          ? SwitchEditorsInfoboxComponent
          : ActionNeededInfoboxComponent,
      ),
    ),
  );

  public pageCtaVisible$ = this.canManageJobs$;
  public viewFormService = signal<ViewFormPublicService | null>(null);
  public sidebarShowMoreThreshold = Number.MAX_SAFE_INTEGER;

  public refreshData$: Observable<void> = merge(
    this.actions$.pipe(
      ofType(
        JobsActions.followSuccess,
        JobsActions.unfollowSuccess,
        JobsActions.createSuccess,
        JobsActions.updateSuccess,
        JobsActions.duplicateSuccess,
        JobsActions.changeStatusSuccess,
        JobsActions.removeOneSuccess,
      ),
    ),
    this.actions$.pipe(
      ofType(JobsActions.updatePositionsSuccess),
      filter(() => !this.viewFormService()?.getValue().sorting?.sortingId),
    ),
    this.actions$.pipe(
      ofType(AsyncJobActions.finish),
      filter(({ payload }) =>
        [
          AsyncJobType.BulkOffersFollow,
          AsyncJobType.BulkOffersUnfollow,
          AsyncJobType.BulkOffersEnableReferralProgram,
          AsyncJobType.BulkOffersDisableReferralProgram,
          AsyncJobType.BulkOffersEnableEeo,
          AsyncJobType.BulkOffersDisableEeo,
          AsyncJobType.BulkOffersDeleteArchived,
          AsyncJobType.BulkOffersRetrieveArchived,
          AsyncJobType.RemoveSampleData,
          AsyncJobType.GenerateSampleData,
          AsyncJobType.BulkWysiwygEditorSwitch,
        ].includes(payload.job.type),
      ),
    ),
  ).pipe(
    map(() => void 0),
    debounceTime(10),
  );

  public translations = {
    dataSourceName: 'app.views_jobs.name',
    pageCta: 'app.views_jobs.view_add_job',
  };

  public defaultEmptyState$: Observable<ViewEmptyState> = this.canManageJobs$.pipe(
    map(canManageJobs => ({
      header: this.i18n.translate('app.views_jobs.default_empty_state.header'),
      description: `${this.i18n.translate(
        'app.views_jobs.default_empty_state.description',
      )}<br><a href="${this.i18n.translate(
        'app.support_resources.create_jobs',
      )}" target="_blank">${this.i18n.translate('app.views_jobs.default_empty_state.see_how')}</a>`,
      icon: 'work',
      button: canManageJobs
        ? {
            iconName: 'add',
            label: this.i18n.translate('app.views_jobs.default_empty_state.button_label'),
            action: () => this.onPageCtaClick(),
          }
        : undefined,
    })),
  );

  public filteredEmptyState$: Observable<ViewEmptyState> = of({
    header: this.i18n.translate('app.views_jobs.filtered_empty_state.header'),
    description: this.i18n.translate('app.views_jobs.filtered_empty_state.description'),
    icon: 'search',
  });

  private notificationsCtrl = inject(NotificationsController);
  private router = inject(Router);
  private analytics = inject(Analytics);
  private jobsAnalytics = inject(JobsAnalytics);
  private jobActionsFacade = inject(JobActionsFacade);
  private untilDestroyed = injectUntilDestroyed();
  private windowRef = inject(WindowRef);

  private sampleDataService = inject(SampleDataService);

  constructor() {
    this.actions$
      .pipe(
        ofType(AsyncJobActions.finish),
        filter(({ payload }) => payload.job.type === AsyncJobType.BulkWysiwygEditorSwitch),
        tap(() => this.router.navigate(['offers/views/jobs:all'], { replaceUrl: true })),
        take(1),
        this.untilDestroyed,
      )
      .subscribe();
  }

  public toggleOfferFollow(offer: { id: number; slug: string; followed: boolean }): void {
    if (!offer.followed) {
      this.ongoingFollow.next({
        ...this.ongoingFollow.value,
        [offer.id]: true,
      });
      this.store.dispatch(
        JobsFullActions.follow({ payload: { id: offer.id, scope: OfferScope.Active } }),
      );
    } else {
      this.store.dispatch(
        JobsFullActions.unfollow({ payload: { id: offer.id, scope: OfferScope.Active } }),
      );
    }

    this.actions$
      .pipe(
        ofType(JobsFullActions.followSuccess, JobsFullActions.unfollowSuccess),
        tap(({ payload: { job } }) => {
          const notificationText = job.followed
            ? {
                title: this.i18n.translate('app.offers.follow_success.job.title'),
                message: this.i18n.translate('app.offers.follow_success.job.message'),
              }
            : this.i18n.translate('app.offers.offer_unfollowed');
          this.notificationsCtrl.success(notificationText);
        }),
        take(1),
      )
      .subscribe(() => {
        this.ongoingFollow.next({
          ...this.ongoingFollow.value,
          [offer.id]: false,
        });
      });
  }

  public onRecordClick(offer: OfferItem): void {
    this.analytics.track('[Jobs] OpenJob', { status: offer.status });
    this.router.navigate(getJobRouterLink(offer));
  }

  public getOfferUrl(offer: OfferItem): string {
    const { origin } = this.windowRef.nativeWindow.location;
    return `${origin}/#/offers/${offer.slug}/pipeline`;
  }

  public onEditClick(offer: OfferItem): void {
    const status = offer.status === OfferStatus.Published ? 'public' : offer.status;
    this.jobsAnalytics.trackEdit(status);

    this.router.navigate(['/offers', offer.slug, 'edit']);
  }

  public onDuplicate(offer: OfferItem) {
    this.jobActionsFacade.duplicate(offer);
  }

  public onArchive(offer: OfferItem) {
    this.jobActionsFacade.archive(offer);
  }

  public onRetrieve(offer: OfferItem) {
    this.jobActionsFacade.retrieve(offer);
  }

  public onDelete(offer: OfferItem) {
    this.jobActionsFacade.delete(offer);
  }

  public onAcquisition(offer: OfferItem): void {
    this.router.navigate(['/offers', offer.slug, 'promote']);
  }

  public onPageCtaClick(): void {
    this.router.navigateByUrl(buildUrlActionTrigger('CreateJobOpen', null, true));
  }

  public onRemoveSampleData(): void {
    this.sampleDataService.onRemoveSampleData(SampleDataRemoveContext.Jobs);
  }
}
